import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

export interface DebounceProps<T> {
  value: T;
  onChange: (value: T) => void;
  delay?: number;
  children: (value: T, setValue: (value: T) => void) => React.ReactNode;
}

export const Debounce = <T,>({ value: initialValue, onChange, delay = 300, children }: DebounceProps<T>) => {
  const [value, setValue] = useState<T>(initialValue);

  // If the initial value changes, update the value state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const debouncedOnChange = useMemo(
    () =>
      debounce((newValue: T) => {
        onChange(newValue);
      }, delay),
    [onChange, delay]
  );

  useEffect(() => {
    debouncedOnChange(value);
    return () => {
      debouncedOnChange.cancel();
    };
  }, [value, debouncedOnChange]);

  return <>{children(value, setValue)}</>;
};
